import { supabase } from '../lib/supabase';

/**
 * Product Service - Handles all vacuum product-related operations with Supabase
 * Converts between snake_case (database) and camelCase (React)
 */

// Helper function to convert snake_case to camelCase
const toCamelCase = (obj) => {
  if (Array.isArray(obj)) {
    return obj?.map(toCamelCase);
  } else if (obj !== null && obj?.constructor === Object) {
    return Object.keys(obj)?.reduce((result, key) => {
      const camelKey = key?.replace(/_([a-z])/g, (g) => g?.[1]?.toUpperCase());
      result[camelKey] = toCamelCase(obj?.[key]);
      return result;
    }, {});
  }
  return obj;
};

// Helper function to convert camelCase to snake_case
const toSnakeCase = (obj) => {
  if (Array.isArray(obj)) {
    return obj?.map(toSnakeCase);
  } else if (obj !== null && obj?.constructor === Object) {
    return Object.keys(obj)?.reduce((result, key) => {
      const snakeKey = key?.replace(/[A-Z]/g, (letter) => `_${letter?.toLowerCase()}`);
      result[snakeKey] = toSnakeCase(obj?.[key]);
      return result;
    }, {});
  }
  return obj;
};

export const productService = {
  /**
   * Get all active products with calculated ratings
   */
  async getAllProducts() {
    try {
      const { data, error } = await supabase?.from('products')?.select(`
          *,
          reviews(rating)
        `)?.eq('is_active', true)?.order('created_at', { ascending: false });

      if (error) throw error;

      // Calculate average rating and review count for each product
      const productsWithRatings = data?.map(product => {
        const reviews = product?.reviews || [];
        const totalReviews = reviews?.length;
        const averageRating = totalReviews > 0
          ? reviews?.reduce((sum, r) => sum + r?.rating, 0) / totalReviews
          : 0;

        const { reviews: _, ...productData } = product;

        return toCamelCase({
          ...productData,
          rating: Number(averageRating?.toFixed(1)),
          review_count: totalReviews
        });
      });

      return productsWithRatings;
    } catch (error) {
      console.error('Error fetching products:', error);
      throw error;
    }
  },

  /**
   * Get product by slug with all related data including affiliate links and AI-generated review
   */
  async getProductBySlug(slug) {
    try {
      const { data, error } = await supabase?.from('products')?.select(`
          *,
          expert_ratings(*),
          reviews(*, review_images(*)),
          testing_methodologies(*),
          product_pros_cons(*),
          video_reviews(*),
          product_reviews!product_reviews_product_id_fkey(*)
        `)?.eq('slug', slug)?.eq('is_active', true)?.maybeSingle();

      if (error) throw error;
      if (!data) return null;

      // Calculate average rating
      const reviews = data?.reviews || [];
      const totalReviews = reviews?.length;
      const averageRating = totalReviews > 0
        ? reviews?.reduce((sum, r) => sum + r?.rating, 0) / totalReviews
        : 0;

      // Sort expert ratings, testing methodologies, and pros/cons
      const sortedExpertRatings = (data?.expert_ratings || [])?.sort((a, b) => 
        a?.created_at?.localeCompare(b?.created_at)
      );

      const sortedTestingMethodologies = (data?.testing_methodologies || [])?.sort((a, b) => 
        a?.display_order - b?.display_order
      );

      const pros = (data?.product_pros_cons || [])?.filter(item => item?.is_pro)?.sort((a, b) => a?.display_order - b?.display_order);

      const cons = (data?.product_pros_cons || [])?.filter(item => !item?.is_pro)?.sort((a, b) => a?.display_order - b?.display_order);

      const sortedReviews = reviews?.sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      );

      // affiliate_links is already a column in products table (JSONB)
      // Parse it if it's a string, otherwise use as-is
      const affiliateLinks = typeof data?.affiliate_links === 'string' 
        ? JSON.parse(data?.affiliate_links) 
        : (data?.affiliate_links || []);

      // Get published AI-generated product review
      const productReview = (data?.product_reviews || [])?.find(r => r?.is_published) || null;

      return toCamelCase({
        ...data,
        rating: Number(averageRating?.toFixed(1)),
        review_count: totalReviews,
        expert_ratings: sortedExpertRatings,
        reviews: sortedReviews,
        testing_methodologies: sortedTestingMethodologies,
        pros,
        cons,
        video_review: data?.video_reviews?.[0] || null,
        affiliate_links: affiliateLinks,
        product_review: productReview
      });
    } catch (error) {
      console.error('Error fetching product:', error);
      throw error;
    }
  },

  /**
   * Get related products (same category, excluding current product)
   */
  async getRelatedProducts(productId, category, limit = 3) {
    try {
      const { data, error } = await supabase?.from('products')?.select(`*,reviews(rating)`)?.eq('category', category)?.eq('is_active', true)?.neq('id', productId)?.limit(limit);

      if (error) throw error;

      const relatedProducts = data?.map(product => {
        const reviews = product?.reviews || [];
        const totalReviews = reviews?.length;
        const averageRating = totalReviews > 0
          ? reviews?.reduce((sum, r) => sum + r?.rating, 0) / totalReviews
          : 0;

        const { reviews: _, ...productData } = product;

        return toCamelCase({
          ...productData,
          rating: Number(averageRating?.toFixed(1)),
          review_count: totalReviews
        });
      });

      return relatedProducts;
    } catch (error) {
      console.error('Error fetching related products:', error);
      throw error;
    }
  },

  /**
   * Subscribe to real-time updates for reviews
   */
  subscribeToReviews(productId, callback) {
    const channel = supabase?.channel(`reviews-${productId}`)?.on(
        'postgres_changes',
        {
          event: '*',schema: 'public',table: 'reviews',
          filter: `product_id=eq.${productId}`
        },
        (payload) => {
          callback(toCamelCase(payload));
        }
      )?.subscribe();

    return () => {
      supabase?.removeChannel(channel);
    };
  }
};

export default productService;